/**
 * What the mic says, and whether it keeps listening, after each outcome.
 *
 * Pulled out of VoiceInput's try/catch because the decisions are real ones and
 * easy to get backwards: a busy provider should NOT stop the recording (the
 * next chunk will probably succeed), while an exhausted quota must, since every
 * further second is audio the server will refuse to transcribe.
 */

import { QuotaExhaustedError, TranscriptionFailedError } from '@/lib/data/voice';
import { quotaMessage, type QuotaStatus } from '@/lib/voice/quota';

export interface Feedback {
  message: string | null;
  tone: 'info' | 'warn';
  /** Whether recording should end now. */
  stop: boolean;
  /**
   * Switch to on-device dictation for the rest of this session.
   *
   * Only ever set for a SERVER-SIDE problem — an outage or a deployment whose
   * keys aren't set up. Never for an exhausted quota: on-device dictation
   * costs us nothing, so falling back there would quietly turn the 60-minute
   * free tier into an unlimited one.
   */
  fallback: boolean;
  /** The quota this outcome revealed, when it carried one. */
  quota?: QuotaStatus | null;
}

/** Outcome of a chunk that transcribed successfully. */
export function feedbackForSuccess(quota: QuotaStatus | null): Feedback {
  const exhausted = quota?.allowed === false;
  return {
    message: quotaMessage(quota),
    tone: exhausted ? 'warn' : 'info',
    // The allowance ran out mid-recording; keeping the mic open would record
    // audio the server has already said it will not accept.
    stop: exhausted,
    fallback: false,
    quota,
  };
}

/** Outcome of a chunk that failed. */
export function feedbackForError(err: unknown, localAvailable = false): Feedback {
  if (err instanceof QuotaExhaustedError) {
    // Deliberately no fallback. See the note on Feedback.fallback.
    return {
      message: quotaMessage(err.quota), tone: 'warn',
      stop: true, fallback: false, quota: err.quota,
    };
  }

  if (err instanceof TranscriptionFailedError) {
    if (err.unconfigured) {
      return localAvailable
        ? {
            message: 'Using your device\u2019s own dictation \u2014 accuracy may be lower.',
            tone: 'info', stop: false, fallback: true,
          }
        : {
            message: 'Dictation is not set up on this server yet.',
            tone: 'warn', stop: true, fallback: false,
          };
    }
    if (err.network) {
      // No connection. The device's dictation needs one too (web-speech.ts:
      // it's Apple's or Google's service), so switching would only fail
      // again. Keep recording; the clip is kept and sent when we're back.
      return {
        message: 'No connection — still recording. Your words will appear when you’re back online.',
        tone: 'warn', stop: false, fallback: false,
      };
    }
    if (err.retryable) {
      // An outage. Switch engines rather than lose the dictation; without a
      // local engine, keep recording since the next chunk may well succeed.
      return localAvailable
        ? {
            message: 'Transcription service is down \u2014 switched to your device\u2019s dictation. Accuracy may be lower.',
            tone: 'warn', stop: false, fallback: true,
          }
        : {
            message: 'Transcription service is busy \u2014 that clip was lost. Still recording.',
            tone: 'warn', stop: false, fallback: false,
          };
    }
    // The clip itself was unusable. Another engine would not help.
    return { message: "Couldn't transcribe that clip.", tone: 'warn', stop: false, fallback: false };
  }

  // Unknown faults stop: something is wrong and silently burning the mic is
  // worse than ending the session.
  return { message: 'Dictation failed.', tone: 'warn', stop: true, fallback: false };
}
